import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {useSelector} from 'react-redux';

import {ApiContext} from '../../../api/Api';

const CARDS_LOADING_ID =
    '@DigitalBusinessCardAdmin.employeePublicCards.get';

const CARD_SAVING_ID =
    '@DigitalBusinessCardAdmin.employeePublicCards.edit';

const normalizeText = value => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const DigitalBusinessCardAdmin = () => {
    const api = useContext(ApiContext);

    const cards = useSelector(
        state => state.api.employeePublicCardsAdmin || []
    );

    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [changingCardId, setChangingCardId] = useState(null);

    const loadCards = useCallback(() => {
        setLoading(true);

        return api.employeePublicCards.get({
            pageSize: 1000,
            loadingId: CARDS_LOADING_ID,
            filters: {
                'order[id]': 'DESC'
            },
            customProp: 'employeePublicCardsAdmin'
        })
            .finally(() => {
                setLoading(false);
            });
    }, [api]);

    useEffect(() => {
        loadCards();
    }, [loadCards]);

    const filteredCards = useMemo(() => {
        const query = normalizeText(search);

        if (!query) {
            return cards;
        }

        return cards.filter(card => {
            const employee = card.employee || {};

            const position = employee.position
                ? employee.position.name
                : '';

            const area = employee.area
                ? employee.area.name
                : '';

            const haystack = normalizeText([
                employee.fullName,
                position,
                area
            ].join(' '));

            return haystack.includes(query);
        });
    }, [cards, search]);

    const totals = useMemo(() => {
        return cards.reduce(
            (result, card) => {
                result.total += 1;

                if (card.isEnabled) {
                    result.active += 1;
                } else {
                    result.inactive += 1;
                }

                return result;
            },
            {
                total: 0,
                active: 0,
                inactive: 0
            }
        );
    }, [cards]);

    const toggleCard = useCallback(card => {
        if (!card || changingCardId) {
            return;
        }

        const nextState = !card.isEnabled;

        const action = nextState
            ? 'habilitar'
            : 'deshabilitar';

        const employeeName =
            card.employee && card.employee.fullName
                ? card.employee.fullName
                : 'este colaborador';

        const confirmed = window.confirm(
            `¿Deseas ${action} la tarjeta digital de ${employeeName}?`
        );

        if (!confirmed) {
            return;
        }

        setChangingCardId(card.id);

        api.employeePublicCards.edit({
            id: card.id,
            employeePublicCard: {
                employee: card.employee
                    ? `/api/employees/${card.employee.id}`
                    : null,
                isEnabled: nextState
            },
            loadingId: CARD_SAVING_ID,
            customProp: 'employeePublicCardsAdmin'
        })
            .finally(() => {
                setChangingCardId(null);
            });
    }, [
        api,
        changingCardId
    ]);

    const openCard = card => {
        if (
            !card
            || !card.publicToken
            || !card.isEnabled
        ) {
            return;
        }

        window.open(
            `${window.location.origin}/card/${card.publicToken}`,
            '_blank'
        );
    };

    return (
        <div
            className="container-fluid main-container"
            style={{paddingBottom: '45px'}}
        >
            <div
                style={{
                    maxWidth: '1180px',
                    margin: '0 auto'
                }}
            >
                <div style={{marginBottom: '24px'}}>
                    <h2 style={{marginBottom: '7px'}}>
                        Administración de Tarjetas Digitales
                    </h2>

                    <p
                        className="text-muted"
                        style={{
                            margin: 0,
                            fontSize: '15px'
                        }}
                    >
                        Consulta y controla la publicación de las tarjetas
                        digitales corporativas.
                    </p>
                </div>

                <div className="row">
                    <SummaryCard
                        icon="fa fa-address-card"
                        title="Tarjetas generadas"
                        value={totals.total}
                    />

                    <SummaryCard
                        icon="fa fa-check-circle"
                        title="Activas"
                        value={totals.active}
                    />

                    <SummaryCard
                        icon="fa fa-ban"
                        title="Deshabilitadas"
                        value={totals.inactive}
                    />
                </div>

                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e4e8ed',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(32,45,64,.08)',
                        overflow: 'hidden'
                    }}
                >
                    <div
                        style={{
                            padding: '18px 20px',
                            borderBottom: '1px solid #edf0f4',
                            background: '#fafbfc'
                        }}
                    >
                        <div className="row">
                            <div className="col-md-7">
                                <strong>
                                    Tarjetas de colaboradores
                                </strong>

                                <div
                                    className="text-muted"
                                    style={{
                                        fontSize: '12px',
                                        marginTop: '4px'
                                    }}
                                >
                                    La tarjeta se genera cuando el colaborador
                                    ingresa por primera vez a Tarjeta Digital.
                                </div>
                            </div>

                            <div className="col-md-5">
                                <div
                                    style={{
                                        position: 'relative'
                                    }}
                                >
                                    <i
                                        className="fa fa-search"
                                        style={{
                                            position: 'absolute',
                                            left: '12px',
                                            top: '11px',
                                            color: '#9aa4af'
                                        }}
                                    />

                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar colaborador, puesto o área..."
                                        value={search}
                                        onChange={
                                            event =>
                                                setSearch(event.target.value)
                                        }
                                        style={{
                                            paddingLeft: '34px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{padding: '20px'}}>
                        {loading ? (
                            <div className="text-center">
                                <i className="fa fa-spinner fa-spin" />
                                {' '}
                                Cargando tarjetas...
                            </div>
                        ) : null}

                        {!loading && !filteredCards.length ? (
                            <div
                                className="alert alert-info"
                                style={{marginBottom: 0}}
                            >
                                <i
                                    className="fa fa-info-circle"
                                    style={{marginRight: '8px'}}
                                />

                                {search
                                    ? 'No encontramos tarjetas que coincidan con la búsqueda.'
                                    : 'Todavía no existen tarjetas digitales generadas.'}
                            </div>
                        ) : null}

                        {!loading && filteredCards.length ? (
                            <div
                                className="table-responsive"
                                style={{marginBottom: 0}}
                            >
                                <table
                                    className="table table-hover"
                                    style={{marginBottom: 0}}
                                >
                                    <thead>
                                        <tr>
                                            <th>Colaborador</th>
                                            <th>Puesto / Área</th>
                                            <th className="text-center">
                                                Estado
                                            </th>
                                            <th className="text-right">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredCards.map(card => (
                                            <CardRow
                                                key={card.id}
                                                card={card}
                                                changing={
                                                    changingCardId === card.id
                                                }
                                                onToggle={toggleCard}
                                                onOpen={openCard}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SummaryCard = ({
    icon,
    title,
    value
}) => (
    <div className="col-sm-4">
        <div
            style={{
                background: '#fff',
                border: '1px solid #e4e8ed',
                borderRadius: '11px',
                padding: '17px 19px',
                marginBottom: '20px',
                boxShadow: '0 3px 12px rgba(32,45,64,.06)'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <div
                    style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '50%',
                        background: '#f1f6fb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '13px',
                        color: '#337ab7',
                        fontSize: '18px'
                    }}
                >
                    <i className={icon} />
                </div>

                <div>
                    <div
                        className="text-muted"
                        style={{fontSize: '12px'}}
                    >
                        {title}
                    </div>

                    <strong
                        style={{
                            fontSize: '23px',
                            fontWeight: 600
                        }}
                    >
                        {value}
                    </strong>
                </div>
            </div>
        </div>
    </div>
);

const CardRow = ({
    card,
    changing,
    onToggle,
    onOpen
}) => {
    const employee = card.employee || {};

    const position = employee.position
        ? employee.position.name
        : 'Puesto no disponible';

    const area = employee.area
        ? employee.area.name
        : 'Área no disponible';

    return (
        <tr>
            <td style={{verticalAlign: 'middle'}}>
                <strong>
                    {employee.fullName || 'Colaborador sin nombre'}
                </strong>
            </td>

            <td style={{verticalAlign: 'middle'}}>
                <div>{position}</div>

                <small className="text-muted">
                    {area}
                </small>
            </td>

            <td
                className="text-center"
                style={{verticalAlign: 'middle'}}
            >
                <span
                    className={
                        card.isEnabled
                            ? 'label label-success'
                            : 'label label-default'
                    }
                    style={{
                        borderRadius: '10px',
                        padding: '5px 9px'
                    }}
                >
                    {card.isEnabled
                        ? 'Activa'
                        : 'Deshabilitada'}
                </span>
            </td>

            <td
                className="text-right"
                style={{
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap'
                }}
            >
                <button
                    type="button"
                    className={
                        card.isEnabled
                            ? 'btn btn-warning btn-sm'
                            : 'btn btn-success btn-sm'
                    }
                    disabled={changing}
                    onClick={() => onToggle(card)}
                    style={{marginRight: '7px'}}
                >
                    <i
                        className={
                            changing
                                ? 'fa fa-spinner fa-spin'
                                : card.isEnabled
                                    ? 'fa fa-ban'
                                    : 'fa fa-check'
                        }
                        style={{marginRight: '6px'}}
                    />

                    {card.isEnabled
                        ? 'Deshabilitar'
                        : 'Habilitar'}
                </button>

                <button
                    type="button"
                    className="btn btn-default btn-sm"
                    disabled={!card.isEnabled}
                    onClick={() => onOpen(card)}
                    title={
                        card.isEnabled
                            ? 'Abrir tarjeta pública'
                            : 'La tarjeta debe estar activa para abrirse'
                    }
                >
                    <i
                        className="fa fa-external-link"
                        style={{marginRight: '6px'}}
                    />
                    Abrir
                </button>
            </td>
        </tr>
    );
};

export default DigitalBusinessCardAdmin;
